import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import Loading from "../../components/loading";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  getCategories,
  addCategory,
  updateCategory,
  deleteCategory,
} from "../../services/services";
import { X } from "lucide-react";
import { confirmDelete } from "../../components/confirmDelete";
import DataTable from "react-data-table-component";

const ServiceCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: "",
    status: "active",
  });

  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);

  const fetchServiceCategories = async () => {
    const payload = { type: "service" };
    try {
      setLoading(true);
      const res = await getCategories(payload);
      if (res.data.status) {
        setCategories(res.data.response || []);
      }
    } catch (err) {
      toast.error("Failed to load service categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceCategories();
  }, []);
  const validate = () => {
    const newErrors = {};

    if (!newCategory.name.trim()) newErrors.name = "Service name is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];

      toast.error(newErrors[firstErrorKey], {
        autoClose: 2500,
      });
      const focusMap = {
        name: nameRef,
      };

      focusMap[firstErrorKey]?.current?.focus();
      return false;
    }

    return true;
  };
  const handleSaveCategory = async () => {
    if (!validate()) {
      // toast.error("Please fix the highlighted errors");
      return;
    }
    if (!newCategory.name.trim()) {
      return toast.error("Category name is required");
    }

    const payload = {
      name: newCategory.name.trim(),
      type: "service", // Fixed for services
      status: newCategory.status,
    };

    try {
      if (editingId) {
        const res = await updateCategory(editingId, payload);
        if (res.data.status) {
          toast.success("Category updated successfully");
          setEditingId(null);
        }
      } else {
        const res = await addCategory(payload);
        if (res.data.status) {
          toast.success(`${res.data.response.name} added!`);
        }
      }

      setNewCategory({ name: "", status: "active" });
      setShowAddModal(false);
      fetchServiceCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save category");
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat._id);
    setNewCategory({ name: cat.name, status: cat.status });
    setShowAddModal(true);
  };

  const handleDelete = async (id, name) => {
    const isConfirm = await confirmDelete(name);
    if (!isConfirm) return;
    try {
      const res = await deleteCategory(id);
      if (res.data.status) {
        toast.success("Category deleted");
        fetchServiceCategories();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Cannot delete — used in services",
      );
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingId(null);
    setNewCategory({ name: "", status: "active" });
  };

  const columns = useMemo(
    () => [
      {
        name: "S.No",
        width: "80px",
        cell: (_, index) => index + 1,
      },
      {
        name: "Category Name",
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: "Status",
        cell: (row) => (
          <span
            className={`rounded-pill text-light px-2 capitalize ${
              row.status === "active" ? "bg-success" : "bg-secondary"
            }`}
          >
            {row.status}
          </span>
        ),
      },
      {
        name: "Actions",
        width: "150px",
        cell: (row) => (
          <div>
            <button
              className="btn btn-light btn-sm shadow-sm me-2 text-primary border"
              onClick={() => handleEdit(row)}
            >
              <i className="bi bi-pencil"></i>
            </button>

            <button
              className="btn btn-light btn-sm shadow-sm text-danger border"
              onClick={() => handleDelete(row._id, row.name)}
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        textTransform: "uppercase",
      },
    },
  };

  if (loading && categories.length === 0) return <Loading />;

  return (
    <>
      <div id="main">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="custom-title mb-0">Service Categories</h4>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => setShowAddModal(true)}
          >
            Add Category
          </button>
        </div>

        <div className="card shadow-sm ">
          <div className="card-body p-0 rounded">
            <DataTable
              columns={columns}
              data={categories}
              progressPending={loading}
              progressComponent={<Loading />}
              customStyles={customStyles}
              striped
              highlightOnHover
              noDataComponent={
                <div className="text-center p-5">
                  <i className="bi bi-tags display-1 text-muted"></i>
                  <p className="py-4 text-gray-500">No categories yet.</p>
                </div>
              }
            />
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-bold">
                  {editingId
                    ? "Edit Service Category"
                    : "Add New Service Category"}
                </h5>
                <X
                  onClick={closeModal}
                  size={24}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <div className="modal-body pt-2">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Category Name
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Home Cleaning, AC Repair, Pest Control"
                    value={newCategory.name}
                    onChange={(e) =>
                      setNewCategory({ ...newCategory, name: e.target.value })
                    }
                    autoFocus
                    ref={nameRef}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Status</label>
                  <select
                    className="form-select"
                    value={newCategory.status}
                    onChange={(e) =>
                      setNewCategory({ ...newCategory, status: e.target.value })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer border-0 pt-2">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveCategory}
                >
                  {editingId ? "Update" : "Add"} Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default headerLayoutWrapper(ServiceCategory);
